import { useCallback } from 'react'
import { useT } from '@transifex/react'
import { ApiRetailerPaymentType } from '@vend/types'
import VendNumber, { round, subtract, vn } from 'vend-number'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { PAYMENT_TYPES } from '@vend/business-components'
import { PartialPaymentOption, SalePayment } from '../PaymentModal/types'
import { selectCurrentSaleAsSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../../../state/hooks'
import { getOutstandingBalance } from '../../../../functions/sale'
import { customerBalancesManager } from '../../../../managers/customer-balances-manager'
import { usePaymentAmountForCashDiscounting } from './useGetPaymentAmountForCashDiscounting'

export const useHandleLoyaltyPayment = (
  amountTendered: string,
  onPaymentSuccess: (result: SalePayment) => void
) => {
  const t = useT()
  const currentSale = useAppSelector(selectCurrentSaleAsSale)

  const {
    getPaymentAmountForCashDiscounting,
  } = usePaymentAmountForCashDiscounting()

  const getAvailableLoyaltyBalance = useCallback((): number => {
    const totalLoyaltyPartialPayments = customerBalancesManager.getTotalPartialPayments(
      PAYMENT_TYPES.LOYALTY,
      currentSale
    )

    if (!(currentSale && currentSale.customer)) {
      return 0
    }
    const loyaltyBalance = currentSale.customer!.loyalty_balance

    return Number(
      round(
        subtract(loyaltyBalance, totalLoyaltyPartialPayments),
        2,
        VendNumber.ROUND_DOWN
      )
    )
  }, [currentSale])

  const handleLoyaltyPayment = useCallback(
    (paymentType: ApiRetailerPaymentType): PartialPaymentOption | undefined => {
      const amountToPay = getPaymentAmountForCashDiscounting(amountTendered)
      const availableLoyalty = getAvailableLoyaltyBalance()

      if (vn(amountToPay).gt(getOutstandingBalance(currentSale))) {
        ToastNotifications.negative(
          t(
            'Amount tendered exceeds payment balance. Please change the amount to pay with loyalty, or choose another payment type.',
            {
              _context: 'Pay with loyalty error message',
            }
          )
        )
        return
      }

      // If the tendered amount is greater than the available loyalty balance,
      // allow the customer to make a partial payment
      if (vn(amountToPay).gt(availableLoyalty)) {
        return {
          paymentModalType: 'partialPayment',
          paymentType,
          paymentTypeName: 'Loyalty',
          availableCredit: availableLoyalty,
        }
      }

      onPaymentSuccess({ paymentAmount: amountToPay!, paymentType })
    },
    [
      amountTendered,
      currentSale,
      getPaymentAmountForCashDiscounting,
      getAvailableLoyaltyBalance,
      onPaymentSuccess,
      t,
    ]
  )
  return {
    handleLoyaltyPayment,
  }
}
